import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard =
      /** @type {import('./product-card').ProductCard | null} */
      (this.closest('product-card'));

    if (productCard) return productCard.productPageUrl;

    const hotspotProduct =
      /** @type {import('./product-hotspot').ProductHotspotComponent | null} */
      (
        this.closest('product-hotspot-component')
      );

    const productLink =
      hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null}
   */
  #getSelectedVariantId() {
    const productCard =
      /** @type {import('./product-card').ProductCard | null} */
      (this.closest('product-card'));

    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    this.#ensureDecantateQuickAddStyles();

    mediaQueryLarge.addEventListener(
      'change',
      this.#closeQuickAddModal
    );

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartUpdate,
      {
        signal:
          this.#cartUpdateAbortController.signal,
      }
    );

    document.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelectUpdate
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener(
      'change',
      this.#closeQuickAddModal
    );

    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();

    document.removeEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelectUpdate
    );
  }

  /**
   * Estilos finales Quick Add Decantate.
   */
  #ensureDecantateQuickAddStyles() {
    if (
      document.getElementById(
        'decantate-quick-add-overrides'
      )
    ) {
      return;
    }

    const style =
      document.createElement('style');

    style.id =
      'decantate-quick-add-overrides';

    style.textContent = `

      /* ======================================================
         DECANTATE QUICK ADD
         ====================================================== */

      #quick-add-dialog .quick-add-modal {
        padding: 0 !important;
        overflow: hidden !important;

        background: #ffffff !important;
        color: #111111 !important;

        border:
          1px solid rgba(17, 17, 17, 0.12)
          !important;

        box-shadow:
          0 24px 70px
          rgba(0, 0, 0, 0.18)
          !important;
      }


      #quick-add-dialog
      .quick-add-modal__close {
        z-index: 1000 !important;
      }


      #quick-add-dialog
      .quick-add-modal__content
      .product-information__media
      slideshow-arrows,

      #quick-add-dialog
      .quick-add-modal__content
      .product-information__media
      slideshow-controls,

      #quick-add-dialog
      .quick-add-modal__content
      .product-information__media
      .product-media-container__zoom-button {
        display: none !important;
      }



      /* ======================================================
         FICHA COMPLETA
         ====================================================== */

      #quick-add-dialog
      .decantate-quick-add-details,

      #quick-add-dialog
      .decantate-quick-add-details * {
        display: flex !important;
      }


      #quick-add-dialog
      .decantate-quick-add-details {
        width: 100% !important;

        margin-top: 18px !important;

        padding-inline:
          var(--padding-3xl)
          !important;

        flex: 0 0 auto !important;
      }


      #quick-add-dialog
      .decantate-quick-add-details
      a {
        width: fit-content !important;

        color: inherit !important;

        font-size: 12px !important;
        font-weight: 650 !important;

        line-height: 1.3 !important;

        text-decoration: none !important;

        border-bottom:
          1px solid currentColor
          !important;

        padding-bottom: 2px !important;
      }



      /* ======================================================
         DESKTOP
         ====================================================== */

      @media screen and (min-width: 750px) {

        #quick-add-dialog
        .quick-add-modal {
          width:
            min(
              1080px,
              calc(100vw - 60px)
            )
            !important;

          height:
            min(
              580px,
              calc(100vh - 70px)
            )
            !important;

          max-width: 1080px !important;
          max-height: 580px !important;

          border-radius: 18px !important;
        }


        #quick-add-dialog
        .quick-add-modal[open] {
          display: flex !important;
        }


        /* ==============================
           ESTRUCTURA
           ============================== */

        #quick-add-dialog
        .quick-add-modal__content {
          display: grid !important;

          grid-template-columns:
            minmax(0, 47%)
            minmax(0, 53%)
            !important;

          grid-template-rows:
            minmax(0, 1fr)
            !important;

          width: 100% !important;
          height: 100% !important;

          max-height: none !important;

          padding: 0 !important;

          gap: 0 !important;

          overflow: hidden !important;
        }



        /* ==============================
           IMAGEN
           ============================== */

        #quick-add-dialog
        .quick-add-modal__content
        .product-information__media {
          grid-column: 1 !important;
          grid-row: 1 !important;

          position: relative !important;

          top: auto !important;

          width: 100% !important;
          height: 100% !important;

          min-width: 0 !important;
          min-height: 0 !important;

          overflow: hidden !important;

          background:
            #f5f5f5
            !important;
        }


        #quick-add-dialog
        .quick-add-modal__content
        .product-information__media
        media-gallery {
          position: absolute !important;

          inset: 0 !important;

          width: 100% !important;
          height: 100% !important;

          padding: 0 !important;
          margin: 0 !important;

          overflow: hidden !important;

          pointer-events: none !important;
        }


        #quick-add-dialog
        .quick-add-modal__content
        .product-information__media
        slideshow-component,

        #quick-add-dialog
        .quick-add-modal__content
        .product-information__media
        slideshow-container,

        #quick-add-dialog
        .quick-add-modal__content
        .product-information__media
        slideshow-slides,

        #quick-add-dialog
        .quick-add-modal__content
        .product-information__media
        slideshow-slide,

        #quick-add-dialog
        .quick-add-modal__content
        .product-information__media
        .media-gallery__grid,

        #quick-add-dialog
        .quick-add-modal__content
        .product-information__media
        .product-media-container,

        #quick-add-dialog
        .quick-add-modal__content
        .product-information__media
        .product-media,

        #quick-add-dialog
        .quick-add-modal__content
        .product-information__media
        .product-media
        > * {
          width: 100% !important;
          height: 100% !important;

          min-height: 0 !important;
          max-height: none !important;

          margin: 0 !important;
          padding: 0 !important;

          gap: 0 !important;

          overflow: hidden !important;

          aspect-ratio:
            auto
            !important;
        }


        #quick-add-dialog
        .quick-add-modal__content
        .product-information__media
        slideshow-slides {
          display: block !important;
        }


        #quick-add-dialog
        .quick-add-modal__content
        .product-information__media
        slideshow-slide {
          display: block !important;

          opacity: 1 !important;
          visibility: visible !important;

          transform: none !important;
        }


        #quick-add-dialog
        .quick-add-modal__content
        .product-information__media
        img {
          display: block !important;

          width: 100% !important;
          height: 100% !important;

          object-fit:
            contain
            !important;

          object-position:
            center
            !important;

          border-radius: 0 !important;
        }



        /* ==============================
           INFORMACIÓN
           ============================== */

        #quick-add-dialog
        .quick-add-modal__content
        .product-details {
          grid-column: 2 !important;
          grid-row: 1 !important;

          display: flex !important;

          flex-direction:
            column
            !important;

          justify-content:
            flex-start
            !important;

          position: relative !important;

          width: 100% !important;
          height: 100% !important;

          min-width: 0 !important;
          min-height: 0 !important;

          max-height: none !important;

          padding:
            38px 0 28px
            !important;

          overflow-y: auto !important;

          mask-image: none !important;

          background:
            #ffffff
            !important;
        }


        #quick-add-dialog
        .quick-add-modal__content
        .product-details
        > .group-block {
          width: 100% !important;

          min-height: 0 !important;

          padding: 0 !important;

          flex:
            0 0 auto
            !important;
        }


        #quick-add-dialog
        .quick-add-modal__content
        .product-details
        > .group-block
        > .group-block-content {
          display: flex !important;

          flex-direction:
            column
            !important;

          width: 100% !important;

          min-height: 0 !important;

          gap: 0 !important;
        }


        #quick-add-dialog
        .quick-add-modal__content
        :where(
          .product-details
          > .group-block
          > .group-block-content
          > *
        ) {
          padding-inline:
            40px
            !important;
        }



        /* ==============================
           TÍTULO
           ============================== */

        #quick-add-dialog
        .quick-add-modal__content
        .view-product-title {
          display: flex !important;

          margin:
            0 0 10px
            !important;

          padding-block:
            0
            !important;

          padding-inline-end:
            90px
            !important;
        }


        #quick-add-dialog
        .quick-add-modal__content
        .view-product-title
        a {
          color: #111111 !important;

          font-size:
            clamp(
              24px,
              2.3vw,
              31px
            )
            !important;

          font-weight:
            650
            !important;

          line-height:
            1.08
            !important;

          letter-spacing:
            -0.035em
            !important;

          text-decoration:
            none
            !important;

          -webkit-line-clamp:
            2
            !important;
        }



        /* ==============================
           PRECIO
           ============================== */

        #quick-add-dialog
        .quick-add-modal__content
        product-price {
          display: block !important;

          margin:
            0 0 6px
            !important;

          --text-align:
            start;
        }


        #quick-add-dialog
        .quick-add-modal__content
        product-price
        * {
          text-align:
            left
            !important;
        }



        /* ==============================
           TAMAÑOS
           ============================== */

        #quick-add-dialog
        .quick-add-modal__content
        variant-picker,

        #quick-add-dialog
        .quick-add-modal__content
        .variant-picker {
          display: block !important;

          flex:
            0 0 auto
            !important;

          margin:
            0
            !important;

          padding-top:
            24px
            !important;

          padding-bottom:
            10px
            !important;
        }



        /* ==============================
           BOTONES
           ============================== */

        #quick-add-dialog
        .quick-add-modal__content
        .buy-buttons-block,

        #quick-add-dialog
        .quick-add-modal__content
        .buy-buttons-block__bar {
          display: block !important;

          position:
            static
            !important;

          inset:
            auto
            !important;

          flex:
            0 0 auto
            !important;

          margin-top:
            10px
            !important;

          padding-top:
            0
            !important;

          padding-bottom:
            0
            !important;

          background:
            transparent
            !important;

          z-index:
            auto
            !important;
        }


        #quick-add-dialog
        .quick-add-modal__content
        .buy-buttons-block::before,

        #quick-add-dialog
        .quick-add-modal__content
        .buy-buttons-block__bar::before {
          display:
            none
            !important;

          content:
            none
            !important;
        }



        /* ==============================
           FICHA COMPLETA NATIVA
           ============================== */

        #quick-add-dialog
        .quick-add-modal__content
        .view-more-details__wrapper {
          display: flex !important;

          justify-content:
            flex-start
            !important;

          width:
            100%
            !important;

          margin-top:
            18px
            !important;

          padding-bottom:
            0
            !important;
        }


        #quick-add-dialog
        .quick-add-modal__content
        .view-more-details {
          display:
            inline-flex
            !important;

          width:
            fit-content
            !important;

          color:
            #111111
            !important;

          font-size:
            12px
            !important;

          font-weight:
            650
            !important;

          line-height:
            1.3
            !important;

          text-decoration:
            none
            !important;

          border-bottom:
            1px solid currentColor
            !important;

          padding-bottom:
            2px
            !important;
        }


        #quick-add-dialog
        .quick-add-modal__close {
          top:
            14px
            !important;

          right:
            14px
            !important;
        }

      }



      /* ======================================================
         MOBILE
         ====================================================== */

      @media screen and (max-width: 749px) {

        #quick-add-dialog
        .quick-add-modal {
          position:
            fixed
            !important;

          width:
            100%
            !important;

          height:
            auto
            !important;

          max-width:
            100%
            !important;

          max-height:
            90vh
            !important;

          margin:
            auto 0 0
            !important;

          border-radius:
            20px 20px 0 0
            !important;
        }


        #quick-add-dialog
        .quick-add-modal__content {
          display:
            flex
            !important;

          flex-direction:
            column
            !important;

          width:
            100%
            !important;

          height:
            auto
            !important;

          max-height:
            90vh
            !important;

          padding:
            0
            !important;

          gap:
            0
            !important;

          overflow-y:
            auto
            !important;
        }



        /* ==============================
           IMAGEN MOBILE
           ============================== */

        #quick-add-dialog
        .quick-add-modal__content
        .product-information__media {
          position:
            relative
            !important;

          display:
            block
            !important;

          width:
            100%
            !important;

          height:
            210px
            !important;

          min-height:
            210px
            !important;

          flex:
            0 0 210px
            !important;

          overflow:
            hidden
            !important;

          background:
            #f5f5f5
            !important;
        }


        #quick-add-dialog
        .quick-add-modal__content
        .product-information__media
        media-gallery {
          position:
            absolute
            !important;

          inset:
            0
            !important;

          width:
            100%
            !important;

          height:
            100%
            !important;

          padding:
            0
            !important;

          overflow:
            hidden
            !important;
        }


        #quick-add-dialog
        .quick-add-modal__content
        .product-information__media
        slideshow-component,

        #quick-add-dialog
        .quick-add-modal__content
        .product-information__media
        slideshow-container,

        #quick-add-dialog
        .quick-add-modal__content
        .product-information__media
        slideshow-slides,

        #quick-add-dialog
        .quick-add-modal__content
        .product-information__media
        slideshow-slide,

        #quick-add-dialog
        .quick-add-modal__content
        .product-information__media
        .media-gallery__grid,

        #quick-add-dialog
        .quick-add-modal__content
        .product-information__media
        .product-media-container,

        #quick-add-dialog
        .quick-add-modal__content
        .product-information__media
        .product-media,

        #quick-add-dialog
        .quick-add-modal__content
        .product-information__media
        .product-media
        > * {
          width:
            100%
            !important;

          height:
            100%
            !important;

          min-height:
            0
            !important;

          max-height:
            none
            !important;

          margin:
            0
            !important;

          padding:
            0
            !important;

          gap:
            0
            !important;

          overflow:
            hidden
            !important;

          aspect-ratio:
            auto
            !important;
        }


        #quick-add-dialog
        .quick-add-modal__content
        .product-information__media
        img {
          display:
            block
            !important;

          width:
            100%
            !important;

          height:
            100%
            !important;

          object-fit:
            contain
            !important;

          object-position:
            center
            !important;

          border-radius:
            0
            !important;
        }



        /* ==============================
           HEADER MOBILE
           ============================== */

        #quick-add-dialog
        .quick-add-modal__content
        .product-header {
          display:
            flex
            !important;

          flex-direction:
            column
            !important;

          width:
            100%
            !important;

          padding:
            22px 54px 0 20px
            !important;

          gap:
            4px
            !important;
        }


        #quick-add-dialog
        .quick-add-modal__content
        .product-header
        > a {
          color:
            #111111
            !important;

          font-size:
            21px
            !important;

          font-weight:
            650
            !important;

          line-height:
            1.13
            !important;

          letter-spacing:
            -0.025em
            !important;

          text-decoration:
            none
            !important;
        }



        /* ==============================
           VARIANTES MOBILE
           ============================== */

        #quick-add-dialog
        .quick-add-modal__content
        > variant-picker,

        #quick-add-dialog
        .quick-add-modal__content
        > .variant-picker {
          width:
            100%
            !important;

          margin:
            0
            !important;

          padding:
            20px 20px 10px
            !important;
        }



        /* ==============================
           FORMULARIO MOBILE
           ============================== */

        #quick-add-dialog
        .quick-add-modal__content
        > product-form-component {
          width:
            100%
            !important;

          padding-inline:
            20px
            !important;

          padding-bottom:
            22px
            !important;
        }


        #quick-add-dialog
        .quick-add-modal__content
        .buy-buttons-block,

        #quick-add-dialog
        .quick-add-modal__content
        .buy-buttons-block__bar {
          position:
            static
            !important;

          inset:
            auto
            !important;

          margin-top:
            10px
            !important;

          padding-block:
            0
            !important;

          background:
            transparent
            !important;
        }


        #quick-add-dialog
        .quick-add-modal__content
        .buy-buttons-block::before,

        #quick-add-dialog
        .quick-add-modal__content
        .buy-buttons-block__bar::before {
          display:
            none
            !important;

          content:
            none
            !important;
        }


        #quick-add-dialog
        .decantate-quick-add-details {
          margin-top:
            0
            !important;

          padding:
            0 20px 24px
            !important;
        }


        #quick-add-dialog
        .quick-add-modal__close {
          top:
            12px
            !important;

          right:
            12px
            !important;
        }

      }

    `;

    document.head.appendChild(
      style
    );
  }

  /**
   * En Quick Add dejamos solamente
   * la segunda imagen del producto.
   *
   * Decantate:
   * 1 = blanco y negro
   * 2 = color
   *
   * Si solo hay una imagen, utiliza esa.
   *
   * @param {Element} productGrid
   */
  #prepareDecantateQuickAddMedia(
    productGrid
  ) {
    const media =
      productGrid.querySelector(
        '.product-information__media'
      );

    if (!media) {
      return;
    }

    media.setAttribute(
      'data-decantate-quick-media',
      'true'
    );



    /* ======================================================
       GALERÍA SLIDESHOW
       ====================================================== */

    const slides =
      Array.from(
        media.querySelectorAll(
          'slideshow-slide'
        )
      );

    if (slides.length) {
      const selectedSlide =
        slides[1] ||
        slides[0];

      for (
        const slide
        of slides
      ) {
        if (
          slide !==
          selectedSlide
        ) {
          slide.remove();

          continue;
        }

        slide.setAttribute(
          'aria-hidden',
          'false'
        );

        slide.removeAttribute(
          'inert'
        );

        slide.removeAttribute(
          'hidden'
        );

        slide.style.removeProperty(
          'display'
        );

        slide.style.removeProperty(
          'opacity'
        );

        slide.style.removeProperty(
          'visibility'
        );

        slide.style.removeProperty(
          'transform'
        );
      }
    }

    else {

      /* ====================================================
         GALERÍA GRID
         ==================================================== */

      const mediaContainers =
        Array.from(
          media.querySelectorAll(
            '.media-gallery__grid > .product-media-container'
          )
        );

      if (
        mediaContainers.length
      ) {
        const selectedMedia =
          mediaContainers[1] ||
          mediaContainers[0];

        for (
          const item
          of mediaContainers
        ) {
          if (
            item !==
            selectedMedia
          ) {
            item.remove();
          }
        }
      }
    }



    /* Quitamos controles innecesarios */

    media
      .querySelectorAll(
        'slideshow-arrows, slideshow-controls'
      )
      .forEach(
        function (element) {
          element.remove();
        }
      );
  }

  /**
   * Agrega o normaliza
   * "Ver ficha completa".
   *
   * @param {Element} modalContent
   */
  #ensureDecantateDetailsLink(
    modalContent
  ) {
    const existingLink =
      /** @type {HTMLAnchorElement | null} */
      (
        modalContent.querySelector(
          '.view-more-details'
        )
      );

    if (existingLink) {
      existingLink.href =
        this.productPageUrl;

      existingLink.textContent =
        'Ver ficha completa →';

      return;
    }


    if (
      modalContent.querySelector(
        '.decantate-quick-add-details'
      )
    ) {
      return;
    }


    const wrapper =
      document.createElement(
        'div'
      );

    wrapper.className =
      'decantate-quick-add-details';


    const link =
      document.createElement(
        'a'
      );

    link.href =
      this.productPageUrl;

    link.textContent =
      'Ver ficha completa →';


    wrapper.appendChild(
      link
    );


    if (
      isMobileBreakpoint()
    ) {
      modalContent.appendChild(
        wrapper
      );

      return;
    }


    const detailsContent =
      modalContent.querySelector(
        '.product-details > .group-block > .group-block-content'
      )
      ||
      modalContent.querySelector(
        '.product-details'
      );


    if (detailsContent) {
      detailsContent.appendChild(
        wrapper
      );
    }

    else {
      modalContent.appendChild(
        wrapper
      );
    }
  }

  /**
   * Updates quick-add button state
   * when product variant is selected
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelectUpdate = (
    event
  ) => {
    if (
      !(
        event.target instanceof
        HTMLElement
      )
    ) {
      return;
    }

    if (
      event.target.closest(
        'product-card'
      ) !==
      this.closest(
        'product-card'
      )
    ) {
      return;
    }

    if (
      this.dataset
        .usesSellingPlans ===
      'true'
    ) {
      return;
    }


    if (
      this.dataset
        .rendersBothButtons !==
      'true'
    ) {
      return;
    }


    const productOptionsCount =
      this.dataset
        .productOptionsCount;


    let quickAddButton =
      productOptionsCount === '1'
        ? 'add'
        : 'choose';


    if (
      quickAddButton ===
        'add'
      &&
      this
        .#isSelectedVariantUnavailable()
    ) {
      quickAddButton =
        'choose';
    }


    this.setAttribute(
      'data-quick-add-button',
      quickAddButton
    );
  };

  /**
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard =
      /** @type {import('./product-card').ProductCard | null} */
      (
        this.closest(
          'product-card'
        )
      );

    return (
      productCard
        ?.variantPicker
        ?.selectedOption
        ?.dataset
        .optionAvailable
      ===
      'false'
    );
  }

  /**
   * Clears cached content
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders variant picker.
   *
   * @param {Element} newHtml
   */
  #updateVariantPicker(
    newHtml
  ) {
    const modalContent =
      document.getElementById(
        'quick-add-modal-content'
      );

    if (!modalContent) {
      return;
    }


    const variantPicker =
      /** @type {VariantPicker | null} */
      (
        modalContent.querySelector(
          'variant-picker'
        )
      );


    if (!variantPicker) {
      return;
    }


    variantPicker
      .updateVariantPicker(
        newHtml
      );
  }

  /**
   * Handles Quick Add click.
   *
   * @param {Event} event
   */
  handleClick = async (
    event
  ) => {
    event.preventDefault();


    const currentUrl =
      this.productPageUrl;


    if (
      this.dataset
        .usesSellingPlans ===
      'true'
    ) {
      if (currentUrl) {
        window.location.href =
          currentUrl;
      }

      return;
    }



    /* ======================================================
       CACHE
       ====================================================== */

    let productGrid =
      this.#cachedContent.get(
        currentUrl
      );


    if (!productGrid) {
      const html =
        await this.fetchProductPage(
          currentUrl
        );


      if (html) {
        const gridElement =
          html.querySelector(
            '[data-product-grid-content]'
          );


        if (gridElement) {
          productGrid =
            /** @type {Element} */
            (
              gridElement.cloneNode(
                true
              )
            );


          this.#cachedContent.set(
            currentUrl,
            productGrid
          );
        }
      }
    }


    if (productGrid) {
      const freshContent =
        /** @type {Element} */
        (
          productGrid.cloneNode(
            true
          )
        );


      await this
        .updateQuickAddModal(
          freshContent
        );


      this.#updateVariantPicker(
        productGrid
      );
    }


    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );


    if (
      !(
        dialogComponent instanceof
        QuickAddDialog
      )
    ) {
      return;
    }


    const productDetails =
      dialogComponent.querySelector(
        '.product-details'
      );


    const productMedia =
      dialogComponent.querySelector(
        '.product-information__media'
      );


    productDetails?.scrollTo({
      top: 0,
      behavior: 'instant',
    });


    productMedia?.scrollTo({
      top: 0,
      behavior: 'instant',
    });
  }

  /**
   * @param {QuickAddDialog} dialogComponent
   */
  #stayVisibleUntilDialogCloses(
    dialogComponent
  ) {
    this.toggleAttribute(
      'stay-visible',
      true
    );


    dialogComponent
      .addEventListener(
        DialogCloseEvent.eventName,

        () =>
          this.toggleAttribute(
            'stay-visible',
            false
          ),

        {
          once: true,
        }
      );
  }

  #openQuickAddModal = () => {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );


    if (
      !(
        dialogComponent instanceof
        QuickAddDialog
      )
    ) {
      return;
    }


    this.#stayVisibleUntilDialogCloses(
      dialogComponent
    );


    dialogComponent.showDialog();


    const dialog =
      dialogComponent.refs?.dialog;


    if (!dialog) {
      return;
    }


    dialog.addEventListener(
      'animationstart',
      this.#resetScroll.bind(
        this
      ),
      {
        once: true,
      }
    );
  };

  #closeQuickAddModal = () => {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );


    if (
      !(
        dialogComponent instanceof
        QuickAddDialog
      )
    ) {
      return;
    }


    dialogComponent.closeDialog();
  };

  /**
   * Fetches product page.
   *
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(
    productPageUrl
  ) {
    if (!productPageUrl) {
      return null;
    }


    this.#abortController
      ?.abort();


    this.#abortController =
      new AbortController();


    try {
      const response =
        await fetch(
          productPageUrl,
          {
            signal:
              this
                .#abortController
                .signal,
          }
        );


      if (!response.ok) {
        throw new Error(
          `Failed to fetch product page: HTTP error ${response.status}`
        );
      }


      const responseText =
        await response.text();


      const html =
        new DOMParser()
          .parseFromString(
            responseText,
            'text/html'
          );


      return html;
    }

    catch (error) {
      if (
        error.name ===
        'AbortError'
      ) {
        return null;
      }

      else {
        throw error;
      }
    }

    finally {
      this.#abortController =
        null;
    }
  }

  /**
   * Re-renders Quick Add.
   *
   * @param {Element} productGrid
   */
  async updateQuickAddModal(
    productGrid
  ) {
    const modalContent =
      document.getElementById(
        'quick-add-modal-content'
      );


    if (
      !productGrid ||
      !modalContent
    ) {
      return;
    }



    /* ======================================================
       DECANTATE

       Esta es la diferencia importante:
       modificamos el contenido ANTES del morph().
       ====================================================== */

    this
      .#prepareDecantateQuickAddMedia(
        productGrid
      );



    /* ======================================================
       MOBILE NATIVO HORIZON
       ====================================================== */

    if (
      isMobileBreakpoint()
    ) {
      const productDetails =
        productGrid.querySelector(
          '.product-details'
        );


      const productFormComponent =
        productGrid.querySelector(
          'product-form-component'
        );


      const variantPicker =
        productGrid.querySelector(
          'variant-picker'
        );


      const productPrice =
        productGrid.querySelector(
          'product-price'
        );


      const productTitle =
        document.createElement(
          'a'
        );


      productTitle.textContent =
        this.dataset.productTitle
        ||
        '';


      productTitle.href =
        this.productPageUrl;


      const productHeader =
        document.createElement(
          'div'
        );


      productHeader.classList.add(
        'product-header'
      );


      productHeader.appendChild(
        productTitle
      );


      if (productPrice) {
        productHeader.appendChild(
          productPrice
        );
      }


      productGrid.appendChild(
        productHeader
      );


      if (variantPicker) {
        productGrid.appendChild(
          variantPicker
        );
      }


      if (
        productFormComponent
      ) {
        productGrid.appendChild(
          productFormComponent
        );
      }


      productDetails?.remove();
    }



    /* ======================================================
       MORPH HORIZON
       ====================================================== */

    const payload =
      productGrid.getAttribute(
        'view-event-payload'
      )
      ||
      '';


    modalContent.setAttribute(
      'view-event-payload',
      payload
    );


    morph(
      modalContent,
      productGrid
    );



    /* ======================================================
       POST PROCESADO DECANTATE
       ====================================================== */

    this.#syncVariantSelection(
      modalContent
    );


    this.#ensureDecantateDetailsLink(
      modalContent
    );
  }

  /**
   * Syncs selected variant.
   *
   * @param {Element} modalContent
   */
  #syncVariantSelection(
    modalContent
  ) {
    const selectedVariantId =
      this.#getSelectedVariantId();


    if (!selectedVariantId) {
      return;
    }


    const modalInputs =
      modalContent
        .querySelectorAll(
          'input[type="radio"][data-variant-id]'
        );


    for (
      const input
      of modalInputs
    ) {
      if (
        input instanceof
          HTMLInputElement
        &&
        input.dataset.variantId ===
          selectedVariantId
        &&
        !input.checked
      ) {
        input.checked =
          true;


        input.dispatchEvent(
          new Event(
            'change',
            {
              bubbles: true,
            }
          )
        );


        break;
      }
    }
  }
}



if (
  !customElements.get(
    'quick-add-component'
  )
) {
  customElements.define(
    'quick-add-component',
    QuickAddComponent
  );
}



class QuickAddDialog
  extends DialogComponent {

  #abortController =
    new AbortController();

  connectedCallback() {
    super.connectedCallback();


    this.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.handleCartUpdate,
      {
        signal:
          this.#abortController.signal,
      }
    );


    this.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );


    this.addEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();


    this.#abortController.abort();


    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose
    );
  }

  /**
   * Closes dialog after cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  handleCartUpdate = (
    event
  ) => {
    event.promise
      ?.then(
        ({ detail }) => {
          if (
            detail?.didError
          ) {
            return;
          }

          this.closeDialog();
        }
      )

      .catch(
        (error) => {
          if (
            error?.name !==
            'AbortError'
          ) {
            console.warn(
              '[quick-add] Event promise rejected:',
              error
            );
          }
        }
      );
  };

  /**
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (
    event
  ) => {
    event.promise
      .then(
        ({ detail }) => {
          if (
            !detail?.html
          ) {
            return;
          }


          const { html } =
            detail;


          const anchorElement =
            /** @type {HTMLAnchorElement} */
            (
              html.querySelector(
                '.view-product-title a'
              )
            );


          const viewMoreDetailsLink =
            /** @type {HTMLAnchorElement} */
            (
              this.querySelector(
                '.view-product-title a'
              )
            );


          const mobileProductTitle =
            /** @type {HTMLAnchorElement} */
            (
              this.querySelector(
                '.product-header a'
              )
            );


          const decantateDetailsLink =
            /** @type {HTMLAnchorElement} */
            (
              this.querySelector(
                '.decantate-quick-add-details a'
              )
            );


          if (!anchorElement) {
            return;
          }


          if (
            viewMoreDetailsLink
          ) {
            viewMoreDetailsLink.href =
              anchorElement.href;
          }


          if (
            mobileProductTitle
          ) {
            mobileProductTitle.href =
              anchorElement.href;
          }


          if (
            decantateDetailsLink
          ) {
            decantateDetailsLink.href =
              anchorElement.href;
          }
        }
      )

      .catch(
        (error) => {
          if (
            error?.name !==
            'AbortError'
          ) {
            console.warn(
              '[quick-add] Event promise rejected:',
              error
            );
          }
        }
      );
  };

  #handleDialogClose = () => {
    const iosVersion =
      getIOSVersion();


    if (
      !iosVersion
      ||
      iosVersion.major >=
        17
      ||
      (
        iosVersion.major ===
          16
        &&
        iosVersion.minor >=
          4
      )
    ) {
      return;
    }


    requestAnimationFrame(
      () => {
        /** @type {HTMLElement | null} */
        const grid =
          document.querySelector(
            '#ResultsList [product-grid-view]'
          );


        if (grid) {
          const currentWidth =
            grid
              .getBoundingClientRect()
              .width;


          grid.style.width =
            `${currentWidth - 1}px`;


          requestAnimationFrame(
            () => {
              grid.style.width =
                '';
            }
          );
        }
      }
    );
  };
}



if (
  !customElements.get(
    'quick-add-dialog'
  )
) {
  customElements.define(
    'quick-add-dialog',
    QuickAddDialog
  );
}